/**
 * Plantafel / Ressourcen-Ansicht — Zeilen = Mitarbeiter (+ „Ohne Zuordnung"),
 * Spalten = Tage (Woche) bzw. Stunden (Tag). Backend-unabhängig: bekommt eine
 * fertige Event-Liste, die sowohl HERO- als auch fortytools-Termine enthalten
 * kann. Klick auf einen Eintrag ruft dessen `onClick` (öffnet das jeweilige
 * Detail-Modal in der CalendarView).
 */
import type { CSSProperties } from "react"

export type PlanRow = { key: string; name: string; color?: string }
export type PlanEvent = {
  id: string
  title: string
  start: Date
  end: Date
  allDay?: boolean
  monteurKeys: string[]   // leer = ohne Zuordnung
  color?: string
  isForty?: boolean
  inOurs?: boolean
  onClick: () => void
}

const HOUR_START = 6
const HOUR_END = 21   // exklusiv-ish; 6–21 = 15 Std Fenster

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}
function dayStart(d: Date): Date { const x = new Date(d); x.setHours(0, 0, 0, 0); return x }
/** Überlappt der Termin [start,end] den Kalendertag `day`? (Mehrtägig-fähig) */
function overlapsDay(day: Date, start: Date, end: Date): boolean {
  const d0 = dayStart(day).getTime()
  const d1 = d0 + 86400000
  return start.getTime() < d1 && end.getTime() > d0
}
function fmtT(d: Date): string {
  return d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })
}
function fmtD(d: Date): string {
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })
}

function Chip({ ev, day }: { ev: PlanEvent; day?: Date }) {
  const bg = ev.color && ev.color.startsWith("linear-gradient") ? undefined : (ev.color || "#64748b")
  const multiDay = !sameDay(ev.start, ev.end)
  // Zeit-/Datumszeile: bei mehrtägigen Terminen je nach Tag „ab …", „ganztägig",
  // „bis …" + Datumsbereich, damit klar ist, dass es über mehrere Tage geht.
  let zeile: string
  if (ev.allDay) {
    zeile = multiDay ? `${fmtD(ev.start)} – ${fmtD(ev.end)}` : "ganztägig"
  } else if (multiDay && day) {
    const isStart = sameDay(day, ev.start)
    const isEnd = sameDay(day, ev.end)
    zeile = isStart ? `ab ${fmtT(ev.start)}` : isEnd ? `bis ${fmtT(ev.end)}` : "ganztägig"
  } else if (multiDay) {
    zeile = `${fmtD(ev.start)} ${fmtT(ev.start)} – ${fmtD(ev.end)} ${fmtT(ev.end)}`
  } else {
    zeile = `${fmtT(ev.start)}–${fmtT(ev.end)}`
  }
  return (
    <button
      type="button"
      onClick={ev.onClick}
      title={`${ev.title}\n${fmtD(ev.start)} ${fmtT(ev.start)} – ${fmtD(ev.end)} ${fmtT(ev.end)}`}
      style={{
        display: "block", width: "100%", textAlign: "left", border: "none", cursor: "pointer",
        background: bg, backgroundImage: ev.color?.startsWith("linear-gradient") ? ev.color : undefined,
        color: "#fff", borderRadius: 5, padding: "3px 6px", marginBottom: 3, lineHeight: 1.25,
        fontSize: 12, overflow: "hidden",
      }}
    >
      <span style={{ fontWeight: 600, display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {ev.title}
        {multiDay && <span style={{ marginLeft: 4, fontSize: 12, background: "rgba(0,0,0,0.35)", padding: "0 3px", borderRadius: 3 }}>mehrtägig</span>}
        {ev.isForty && <span style={{ marginLeft: 4, fontSize: 12, background: ev.inOurs ? "rgba(16,185,129,0.95)" : "rgba(0,0,0,0.35)", padding: "0 3px", borderRadius: 3 }}>{ev.inOurs ? "✓ 40T" : "40T"}</span>}
      </span>
      <span style={{ opacity: 0.9, fontSize: 12 }}>{zeile}</span>
    </button>
  )
}

export function PlanBoard({
  rows, events, mode, days,
}: {
  rows: PlanRow[]
  events: PlanEvent[]
  mode: "week" | "day"
  days: Date[]
}) {
  // Zweizeilig statt abgeschnitten: `-webkit-line-clamp` haelt die Zeile
  // kompakt, `anywhere` bricht auch einen langen Einzelnamen um, statt ihn
  // ueber die Spalte hinauslaufen zu lassen.
  const nameStil: CSSProperties = {
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    overflowWrap: "anywhere",
    lineHeight: 1.25,
  }

  // Events pro Zeile (monteurKey) — Mehrfach-Zuordnung erscheint in jeder Zeile.
  const byRow = (rowKey: string): PlanEvent[] => {
    if (rowKey === "__none__") return events.filter((e) => e.monteurKeys.length === 0)
    return events.filter((e) => e.monteurKeys.includes(rowKey))
  }

  // 🔑 Namen NICHT abschneiden. Bei 118 px hiess Al Shaher Abdulrahman
  // „Al Shaher A…" und Fodor Francisc Catalin „Fodor Fran…" — wer die Tafel
  // liest, muss die Leute aber auseinanderhalten koennen. Jetzt breiter, und
  // was dann noch nicht in eine Zeile passt, bricht auf zwei um (s. `nameStil`).
  const NAMECOL = 150
  // 🔑 Die Tagesspalten waren fest 200 px breit. Mal sieben plus Namensspalte
  // sind das 1.560 px — auf einem normalen Bildschirm war der Samstag
  // angeschnitten und der Sonntag gar nicht zu sehen, obwohl die Woche
  // laengst sieben Tage liefert. Jetzt teilen sie sich die verfuegbare
  // Breite und unterschreiten dabei `DAYMIN` nicht; erst darunter wird
  // wieder gescrollt. Dass ein Chip zu EINEM Tag gehoert, macht die
  // Spaltenlinie klar, nicht die feste Breite.
  // 96 px ist die Untergrenze, bei der auf einem 1280-px-Bildschirm mit
  // eingeklappter Legende alle sieben Tage nebeneinander passen. Breiter wird
  // es von selbst, sobald Platz da ist.
  const DAYMIN = 96

  if (mode === "week") {
    const cols = `${NAMECOL}px repeat(${days.length}, minmax(${DAYMIN}px, 1fr))`
    return (
      <div style={{ overflowX: "auto", border: "1px solid var(--border-color)", borderRadius: 8, background: "var(--bg-card)" }}>
        <div style={{ minWidth: NAMECOL + days.length * DAYMIN }}>
          {/* Kopf */}
          <div style={{ display: "grid", gridTemplateColumns: cols, borderBottom: "2px solid var(--border-color)", position: "sticky", top: 0, background: "var(--bg-card)", zIndex: 1 }}>
            <div style={{ padding: "8px 10px", fontSize: 12, fontWeight: 700, color: "var(--text-muted)" }}>Mitarbeiter</div>
            {days.map((d, i) => (
              <div key={i} style={{ padding: "8px 10px", fontSize: 12.5, fontWeight: 600, textAlign: "center", borderLeft: "2px solid var(--border-color)" }}>
                {d.toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" })}
              </div>
            ))}
          </div>
          {/* Zeilen */}
          {rows.map((r, ri) => {
            const rowEvents = byRow(r.key)
            return (
              <div key={r.key} style={{ display: "grid", gridTemplateColumns: cols, borderBottom: "1px solid var(--border-color)", minHeight: 52, background: ri % 2 ? "transparent" : "rgba(148,163,184,0.05)" }}>
                <div style={{ padding: "6px 10px", display: "flex", alignItems: "center", gap: 7, fontSize: 12.5, fontWeight: 500, position: "sticky", left: 0, background: "var(--bg-card)", zIndex: 1 }}>
                  <span style={{ width: 9, height: 9, borderRadius: "50%", background: r.color || "#94a3b8", flexShrink: 0 }} />
                  <span style={nameStil} title={r.name}>{r.name}</span>
                </div>
                {days.map((d, i) => {
                  const cell = rowEvents.filter((e) => overlapsDay(d, e.start, e.end))
                    .sort((a, b) => a.start.getTime() - b.start.getTime())
                  return (
                    <div key={i} style={{ padding: "5px 7px", borderLeft: "2px solid var(--border-color)" }}>
                      {cell.map((e) => <Chip key={e.id} ev={e} day={d} />)}
                    </div>
                  )
                })}
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  // ── Tag: Zeilen = Mitarbeiter, Spalten = Stunden ──────────────────────────
  const day = days[0]
  const hours: number[] = []
  for (let h = HOUR_START; h <= HOUR_END; h++) hours.push(h)
  const span = HOUR_END - HOUR_START
  const pos = (d: Date): number => {
    const hh = d.getHours() + d.getMinutes() / 60
    return Math.max(0, Math.min(100, ((hh - HOUR_START) / span) * 100))
  }

  return (
    <div style={{ overflowX: "auto", border: "1px solid var(--border-color)", borderRadius: 8, background: "var(--bg-card)" }}>
      <div style={{ minWidth: NAMECOL + 700 }}>
        {/* Stunden-Kopf */}
        <div style={{ display: "grid", gridTemplateColumns: `${NAMECOL}px 1fr`, borderBottom: "2px solid var(--border-color)" }}>
          <div style={{ padding: "8px 10px", fontSize: 12, fontWeight: 700, color: "var(--text-muted)" }}>
            {day.toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "2-digit" })}
          </div>
          <div style={{ position: "relative", height: 28 }}>
            {hours.map((h) => (
              <div key={h} style={{ position: "absolute", left: `${((h - HOUR_START) / span) * 100}%`, top: 0, fontSize: 12, color: "var(--text-muted)", transform: "translateX(-50%)" }}>
                {String(h).padStart(2, "0")}
              </div>
            ))}
          </div>
        </div>
        {/* Zeilen */}
        {rows.map((r) => {
          const rowEvents = byRow(r.key).filter((e) => overlapsDay(day, e.start, e.end)).sort((a, b) => a.start.getTime() - b.start.getTime())
          return (
            <div key={r.key} style={{ display: "grid", gridTemplateColumns: `${NAMECOL}px 1fr`, borderBottom: "1px solid var(--border-color)", minHeight: 44 }}>
              <div style={{ padding: "6px 10px", display: "flex", alignItems: "center", gap: 7, fontSize: 12.5, fontWeight: 500 }}>
                <span style={{ width: 9, height: 9, borderRadius: "50%", background: r.color || "#94a3b8", flexShrink: 0 }} />
                <span style={nameStil} title={r.name}>{r.name}</span>
              </div>
              <div style={{ position: "relative", margin: "4px 0" }}>
                {/* Stunden-Gitter */}
                {hours.map((h) => (
                  <div key={h} style={{ position: "absolute", left: `${((h - HOUR_START) / span) * 100}%`, top: 0, bottom: 0, borderLeft: "1px solid var(--border-color)", opacity: 0.4 }} />
                ))}
                {rowEvents.map((e) => {
                  const left = pos(e.start)
                  const right = pos(e.end)
                  const width = Math.max(3, right - left)
                  const bg = e.color?.startsWith("linear-gradient") ? undefined : (e.color || "#64748b")
                  return (
                    <button
                      key={e.id}
                      type="button"
                      onClick={e.onClick}
                      title={`${e.title}\n${fmtT(e.start)}–${fmtT(e.end)}`}
                      style={{
                        position: "absolute", left: `${left}%`, width: `${width}%`, top: 2, minHeight: 30,
                        background: bg, backgroundImage: e.color?.startsWith("linear-gradient") ? e.color : undefined,
                        color: "#fff", border: "none", borderRadius: 5, padding: "3px 6px", cursor: "pointer",
                        fontSize: 12, textAlign: "left", overflow: "hidden",
                      }}
                    >
                      <span style={{ fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block" }}>
                        {e.title}{e.isForty && <span style={{ marginLeft: 3, fontSize: 12, background: e.inOurs ? "rgba(16,185,129,0.95)" : "rgba(0,0,0,0.35)", padding: "0 3px", borderRadius: 3 }}>{e.inOurs ? "✓" : "40T"}</span>}
                      </span>
                      <span style={{ opacity: 0.9, fontSize: 12 }}>{fmtT(e.start)}–{fmtT(e.end)}</span>
                    </button>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
